import useAsyncModalConfirm from '@/main/components/shared/asyncModalConfirm/asyncModalConfirm';
import Box from '@/main/components/shared/box';
import KogAutoCompleteControlled from '@/main/components/shared/inputAutoComplete/autoCompleteControlled';
import KogInputTextControlledNew from '@/main/components/shared/inputText/inputTextControlledNew';
import { KeyValue } from '@/main/models/common';
import { IApprovalMatrixRegisterForm } from '@/main/screens/registration/approvalMatrix/registerApprovalMatrix/utilsRegisterApprovalMatrix';
import { getOptionsStatus } from '@/main/services/data/data';
import { SystemApplicationEnum } from '@/main/services/enum/SystemApplicationEnum';
import { isNullUndefined } from '@/main/services/reducerUtils';
import {
  getDefaultCountryOptionDrop,
  getDefaultOptionDrop,
  getTranslateByKey
} from '@/main/services/utils';
import { userIsInsurer } from '@/main/services/utilsUser';
import { useApprovalMatrixTypeReducer } from '@/main/store/approvalMatrixType';
import { useCountryReducer } from '@/main/store/country';
import { getInsurers, useInsurerReducer } from '@/main/store/insurer';
import {
  getUserHierarchy,
  resetReducerUserHierarchy
} from '@/main/store/userHierarchy';
import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import * as S from '../styles';

export const ID_BASE_COMPONENTS_REGISTER_MATRIX: string =
  'id-registerApprovalMatrix';
interface IApprovalMatrixInfo {
  isEdit: boolean;
}

export default function ApprovalMatrixInfo({ isEdit }: IApprovalMatrixInfo) {
  const {
    control,
    setValue,
    formState: { errors },
    getValues
  } = useFormContext<IApprovalMatrixRegisterForm>();

  const [getConfirmation, ModalConfirmation] = useAsyncModalConfirm();

  const dispatch = useDispatch();
  const {
    countriesUser: { data: countries, isLoading: isLoadingCountries }
  } = useCountryReducer();
  const {
    insurers: { data: insurers, isLoading: isLoadingInsurers }
  } = useInsurerReducer();
  const {
    approvalMatrixType: {
      data: approvalMatrixTypes,
      isLoading: isLoadingApprovalMatrixType
    }
  } = useApprovalMatrixTypeReducer();

  const statusRegisterOptions = getOptionsStatus();

  const optionDefaultCountryDrop = getDefaultCountryOptionDrop(countries);
  const optionDefaultInsurerDrop = getDefaultOptionDrop(insurers);

  const isDisabledInsurerDrop = userIsInsurer() || isEdit;

  function callInsurerApiDomain(guidCountry: string) {
    dispatch(getInsurers({ guidCountry }));
  }

  function callHierarchyApiDomain(guidInsurer: string) {
    dispatch(getUserHierarchy(guidInsurer, SystemApplicationEnum.Parts));
  }

  useEffect(() => {
    if (!isNullUndefined(optionDefaultCountryDrop?.key)) {
      callInsurerApiDomain(String(optionDefaultCountryDrop.key));
    }
  }, [optionDefaultCountryDrop?.key]);

  useEffect(() => {
    if (!isNullUndefined(optionDefaultInsurerDrop?.key)) {
      callHierarchyApiDomain(String(optionDefaultInsurerDrop.key));
    }
  }, [optionDefaultInsurerDrop?.key]);

  return (
    <Box title={getTranslateByKey('information.approvalInformation')}>
      <S.RowInputs>
        <KogAutoCompleteControlled
          id={`${ID_BASE_COMPONENTS_REGISTER_MATRIX}-select-country`}
          label={getTranslateByKey('information.country')}
          control={control}
          options={countries}
          isLoading={isLoadingCountries}
          nameCustom="approvalMatrixInformation.country"
          onChangeCustom={(optionSelected: KeyValue | null) => {
            if (optionSelected)
              callInsurerApiDomain(String(optionSelected.key));
          }}
          valueDefaultKey={optionDefaultCountryDrop?.key}
          setValueCustom={setValue}
          disabled={optionDefaultCountryDrop !== null}
          required
          rulesCustom={{ required: true }}
          errorMessageCustom={
            errors.approvalMatrixInformation?.country?.message ?? ''
          }
          hasErrorCustom={!!errors.approvalMatrixInformation?.country}
        />

        <KogAutoCompleteControlled
          id={`${ID_BASE_COMPONENTS_REGISTER_MATRIX}-select-insurer`}
          label={getTranslateByKey('information.insurer')}
          control={control}
          options={insurers}
          isLoading={isLoadingInsurers}
          nameCustom="approvalMatrixInformation.insurer"
          setValueCustom={setValue}
          isDefaultValueUnique
          disabled={isDisabledInsurerDrop}
          valueDefaultKey={optionDefaultInsurerDrop?.key}
          required
          rulesCustom={{ required: true }}
          errorMessageCustom={
            errors.approvalMatrixInformation?.insurer?.message ?? ''
          }
          confirmChange={async (optionSelected: KeyValue | null) => {
            const oldValue = getValues('approvalMatrixInformation.insurer');
            const approvalMatrixValues = getValues('approvalMatrixValues');
            const hasMatrixValues =
              approvalMatrixValues && approvalMatrixValues.length > 0;

            if (!isNullUndefined(oldValue) && hasMatrixValues) {
              const confirmed = await getConfirmation(
                getTranslateByKey('information.confirmChangeInsurer')
              );
              return confirmed;
            }

            return true;
          }}
          onChangeCustom={(optionSelected: KeyValue | null) => {
            dispatch(resetReducerUserHierarchy());

            if (optionSelected) {
              callHierarchyApiDomain(String(optionSelected.key));
            }

            setValue('approvalMatrixValues', []);
          }}
          hasErrorCustom={!!errors.approvalMatrixInformation?.insurer}
        />

        <KogInputTextControlledNew
          control={control}
          name="approvalMatrixInformation.matrixName"
          id={`${ID_BASE_COMPONENTS_REGISTER_MATRIX}-text-matrixName`}
          label={getTranslateByKey('information.matrixName')}
          required
        />

        <KogAutoCompleteControlled
          id={`${ID_BASE_COMPONENTS_REGISTER_MATRIX}-select-approvalMatrixType`}
          label={getTranslateByKey('information.approvalMatrixType')}
          control={control}
          options={approvalMatrixTypes}
          isLoading={isLoadingApprovalMatrixType}
          nameCustom="approvalMatrixInformation.approvalMatrixType"
          setValueCustom={setValue}
          isDefaultValueUnique
          onChangeCustom={() => {}}
          required
          rulesCustom={{ required: true }}
          errorMessageCustom={
            errors.approvalMatrixInformation?.approvalMatrixType?.message ?? ''
          }
          hasErrorCustom={
            !!errors.approvalMatrixInformation?.approvalMatrixType
          }
        />

        <KogAutoCompleteControlled
          id={`${ID_BASE_COMPONENTS_REGISTER_MATRIX}-select-statusRegister`}
          label={getTranslateByKey('information.status')}
          nameCustom="approvalMatrixInformation.statusRegister"
          control={control}
          setValueCustom={setValue}
          options={statusRegisterOptions}
          onChangeCustom={() => {}}
          required
          rulesCustom={{ required: true }}
          errorMessageCustom={
            errors.approvalMatrixInformation?.statusRegister?.message ?? ''
          }
          hasErrorCustom={!!errors.approvalMatrixInformation?.statusRegister}
        />
      </S.RowInputs>

      <ModalConfirmation />
    </Box>
  );
}
